import type { SanityEventHero } from '#/lib/sanity/queries/eventHero';
import {
  getSectionButtonTheme,
  getSectionThemeAccent,
  getSectionThemeSurface,
  isDarkSectionTheme,
  normalizeSectionTheme,
} from '#/lib/section-theme';
import { cn, COLORS } from '#/lib/utils';

import { FormCustom } from './Form/Custom';
import { Headline } from './Headline';
import {
  getHeroBackground,
  HeroBackgroundGraphic,
} from './HeroBackgroundGraphic';
import { LogoMarquee } from './LogoMarquee';
import { Media } from './Media';
import { RichText } from './RichText';
import { SocialProof } from './SocialProof/SocialProof';

/**
 * Event-page hero. Same layout as Hero Form, but the form is omitted once the
 * parent event is past (while headline, body, and media stay).
 */
export function EventHero(props: SanityEventHero) {
  const isFirst = props.sectionIndex === 0;
  const theme = normalizeSectionTheme(props.theme, 'charcoal');
  const isDark = isDarkSectionTheme(theme);
  const accentClass = getSectionThemeAccent(theme);
  const formTheme = theme === 'charcoal' ? 'dark' : 'light';
  const background = getHeroBackground(theme, props.backgroundPattern);
  const marqueeBackground =
    background?.baseColor ??
    (theme === 'purple'
      ? COLORS.cipher.DEFAULT
      : theme === 'charcoal'
        ? COLORS.charcoal
        : COLORS.cloud);

  const showForm =
    !props.isPast && props.form != null && props.formConfirmation != null;

  return (
    <div
      className={cn(
        'p-main',
        getSectionThemeSurface(theme),
        background && 'relative isolate overflow-hidden',
        isFirst
          ? 'mt-[calc(var(--header-height)*-1)] pb-100 pt-[calc(var(--header-height)+5rem)]'
          : 'py-100',
      )}
      style={background ? { backgroundColor: background.baseColor } : undefined}
    >
      {background ? <HeroBackgroundGraphic background={background} /> : null}
      <div className="fade-in grid-main mx-auto max-w-full l:max-w-1500">
        <div className="col-span-full l:col-span-6">
          <div className="flex h-full max-w-600 flex-col l:mx-auto l:max-w-490">
            <Headline
              value={props.headline}
              highlightClass={accentClass}
              className="mb-25 text-32 m:text-43 l:text-43"
            />
            <RichText
              value={props.bodyCopy ?? []}
              normalTextSizeClassName={cn(
                'text-body',
                isDark ? 'text-cloud' : 'text-charcoal',
              )}
            />
            <SocialProof socialProof={props.socialProof} />
            {showForm ? (
              <div className="mt-20 flex-1">
                <FormCustom
                  className={cn(
                    'flex h-full flex-col',
                    isDark &&
                      '[&_.legal-text]:!text-cloud [&_.step-indicator]:!text-cloud',
                  )}
                  form={props.form!}
                  theme={formTheme}
                  buttonTheme={getSectionButtonTheme(theme)}
                  confirmation={{
                    ...props.formConfirmation!,
                    headline: undefined,
                  }}
                />
              </div>
            ) : null}
          </div>
        </div>
        <div className="col-span-6 hidden l:col-start-7 l:block">
          <Media media={props.formMedia} sizes="50vw" />
        </div>
      </div>
      {props.logoMarquee ? (
        <LogoMarquee
          {...props.logoMarquee}
          background={marqueeBackground}
          foreground={isDark ? COLORS.cloud : COLORS.charcoal}
          className="mt-60"
        />
      ) : null}
    </div>
  );
}
